import React, { useEffect } from "react";
import { sortItems } from "@/lib/itemSort";

const r2 = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const n3 = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });
const MODES = [
  { key: "per_sqft", label: "Per sqft" },
  { key: "wholesale", label: "Wholesale (kg)" },
  { key: "job_work", label: "Job work" },
];

const isMaster = (m) => String(m.category || "").toLowerCase() === "sheet" && !m.master_item_id
  && m.item_type !== "SERVICE" && m.sheet_status !== "to_confirm" && m.is_active !== false;

// Sheet Cutting dropdown: active master sheets first, then SERVICE items (no stock).
// New Order dropdown: ALL active items grouped SHEETS | HARDWARE | SERVICE.
// Hides inactive + linked duplicates; saved item always kept (marked "old item").
export function orderPickerItems(all, currentId, { service = true } = {}) {
  const live = all.filter((m) => m.is_active !== false && !m.master_item_id && m.sheet_status !== "to_confirm");
  const isSvc = (m) => m.item_type === "SERVICE" || m.is_service;
  const isHw = (m) => String(m.category || "").toLowerCase() === "hardware";
  const sheets = sortItems(live.filter((m) => !isSvc(m) && !isHw(m))).map((m) => ({ ...m, group: "SHEETS" }));
  const hw = sortItems(live.filter((m) => !isSvc(m) && isHw(m))).map((m) => ({ ...m, group: "HARDWARE" }));
  const svc = service ? sortItems(live.filter(isSvc)).map((m) => ({ ...m, group: "SERVICE" })) : [];
  const out = [...sheets, ...hw, ...svc];
  if (currentId && !out.some((m) => m.id === currentId)) {
    const cur = all.find((m) => m.id === currentId);
    if (cur) out.push({ ...cur, name: `${cur.name} (old item)`, group: "OLD ITEM" });
  }
  return out;
}

export function sheetPickerItems(all, currentId) {
  const masters = sortItems(all.filter(isMaster));
  const services = all.filter((m) => m.item_type === "SERVICE").map((m) => ({ ...m, name: `${m.name} · SERVICE (no stock)` }));
  const cur = currentId && ![...masters, ...services].some((m) => m.id === currentId) ? all.filter((m) => m.id === currentId) : [];
  return [...masters, ...services, ...cur];
}

export function BillingModeBar({ mode, setMode, disabled, wKg, setWKg, wRate, setWRate, items, materialItems, isAdmin }) {
  const first = materialItems.find((m) => m.id === items.find((it) => it.item_id)?.item_id);
  const avgKg = Number(first?.avg_rate_per_kg || 0);
  useEffect(() => {
    if (mode === "wholesale" && !wRate && avgKg > 0) setWRate(String(Math.round((avgKg + 20) * 100) / 100));
  }, [mode, avgKg]); // eslint-disable-line react-hooks/exhaustive-deps
  const inp = "h-9 w-28 bg-slate-800/60 border border-slate-600 text-white rounded-sm px-2 text-sm font-mono-num";
  return (
    <div className="flex flex-col items-end gap-3" data-testid="billing-mode-bar">
      <div className="inline-flex p-1 rounded-sm bg-slate-700/50 border border-slate-700/60">
        {MODES.map((m) => (
          <button key={m.key} type="button" disabled={disabled} onClick={() => setMode(m.key)}
            className={"px-4 py-1.5 text-sm rounded-sm transition-colors disabled:opacity-60 " +
              (mode === m.key ? "bg-slate-800 text-slate-50 font-medium shadow-sm" : "text-slate-400 hover:text-slate-200")}
            data-testid={`billing-mode-${m.key}`}>{m.label}</button>
        ))}
      </div>
      {mode === "wholesale" && (
        <div className="flex items-end gap-3 flex-wrap" data-testid="wholesale-fields">
          <label className="block"><span className="block text-[10px] font-mono-num text-slate-400 mb-1">WEIGHED KG (KANTA)</span>
            <input type="number" min="0" step="0.001" value={wKg} onChange={(e) => setWKg(e.target.value)} className={inp} data-testid="wholesale-kg" /></label>
          <label className="block"><span className="block text-[10px] font-mono-num text-slate-400 mb-1">RATE / KG (₹)</span>
            <input type="number" min="0" step="0.01" value={wRate} onChange={(e) => setWRate(e.target.value)} className={inp} data-testid="wholesale-rate" /></label>
          <div className="text-sm text-slate-300 pb-2">Bill = <b className="font-mono-num text-orange-300" data-testid="wholesale-bill">{r2((Number(wKg) || 0) * (Number(wRate) || 0))}</b></div>
        </div>
      )}
      {mode === "job_work" && <JobWorkBadge testid="job-work-form-badge" />}
    </div>
  );
}

export function JobWorkBadge({ testid = "job-work-badge", small }) {
  return (
    <div className={`inline-flex items-center rounded-sm bg-red-600 text-white font-mono-num tracking-widest font-bold ${small ? "text-[9px] px-1.5 py-0.5" : "text-[11px] px-2 py-1"}`} data-testid={testid}>
      CUSTOMER MATERIAL — DO NOT USE OUR SHEET
    </div>
  );
}

// (min(rate, MRP) − company base rate) × qty — same rule as backend _stamp_commissions.
export function calcCommission(lines, materialItems) {
  const byId = Object.fromEntries(materialItems.map((m) => [m.id, m]));
  let total = 0;
  lines.forEach((l) => {
    const m = byId[l.item_id];
    const mrp = Number(m?.mrp || 0), cp = Number(m?.company_price || 0);
    if (mrp > 0 && cp > 0 && cp < mrp) total += Math.max(0, Math.min(l.rate, mrp) - cp) * (l.mult ?? l.sqft);
  });
  return Math.round(total * 100) / 100;
}

export function CostPreviewBox({ mode, totals, materialItems, machineCost, welderComm, staffOn, staffAmount, wKg }) {
  const byId = Object.fromEntries(materialItems.map((m) => [m.id, m]));
  let cost = 0, missing = false, raw = 0;
  totals.lines.forEach((l) => {
    const m = byId[l.item_id];
    if (!m || m.item_type === "SERVICE" || m.is_service) return;
    if (!l.sqft) {
      if (Number(m.purchase_price) > 0) cost += (l.mult || 0) * Number(m.purchase_price);
      else if (l.mult > 0) missing = true;
      return;
    }
    if (mode === "job_work") cost += l.sqft * machineCost;
    else if (m.avg_cost_per_sqft == null) missing = true;
    else cost += l.sqft * Number(m.avg_cost_per_sqft);
    const w = Number(m.avg_sheet_weight_kg || m.start_sheet_weight_kg || 0);
    raw += (l.sqft / Number(m.sheet_size_sqft || 32)) * w;
  });
  const staff = staffOn && !(welderComm > 0) ? (staffAmount ?? calcCommission(totals.lines, materialItems)) : 0;
  const commission = staff + (welderComm || 0);
  const profit = totals.amount - cost - commission;
  const row = (k, v, id, tone = "text-slate-200") => (
    <div className="flex justify-between text-xs"><span className="text-slate-400">{k}</span><span className={`font-mono-num ${tone}`} data-testid={id}>{v}</span></div>
  );
  return (
    <div className="mt-2 border border-dashed border-orange-500/70 rounded-sm p-3 space-y-1" data-testid="cost-preview-box">
      <div className="text-[10px] font-mono-num tracking-widest text-orange-300">// COST & PROFIT PREVIEW · ADMIN ONLY</div>
      {row("Bill", r2(totals.amount), "cp-bill")}
      {row(mode === "job_work" ? `Machine cost (${n3(totals.sqft)} sqft × ₹${machineCost})` : "Material cost", missing ? `Cost not recorded${cost > 0 ? ` (+ ${r2(cost)} known)` : ""}` : r2(cost), "cp-cost", missing ? "text-amber-300" : "text-red-300")}
      {mode === "wholesale" && row("Raw kg / scrap kg", `${n3(raw)} / ${n3(Math.max(raw - (Number(wKg) || 0), 0))}`, "cp-raw-scrap")}
      {row("Staff commission", r2(staff), "cp-staff-commission")}
      {row("Welder commission", r2(welderComm || 0), "cp-welder-commission")}
      <div className="border-t border-orange-500/30 pt-1">
        {row("Profit", missing ? "—" : r2(profit), "cp-profit", profit >= 0 ? "text-emerald-300 font-bold" : "text-red-400 font-bold")}
      </div>
      <div className="text-[10px] text-slate-500">GST wale order me final profit GST hata ke banega.</div>
    </div>
  );
}

const costNote = (order, goLiveAt) => (!goLiveAt || String(order?.created_at || "") < String(goLiveAt)
  ? "Cost not recorded (purana order)" : "Cost not recorded — no purchase entered for this sheet yet");

export function CostingSection({ order, goLiveAt }) {
  const cb = order?.cost_breakdown;
  if (!cb) {
    return order?.project_type === "sheet_cutting" || !order?.project_type
      ? <div className="text-xs text-slate-500 italic" data-testid="costing-not-recorded">COSTING · {costNote(order, goLiveAt)}</div> : null;
  }
  const w = cb.wholesale;
  return (
    <div className="border border-dashed border-orange-500/70 rounded-sm p-3 space-y-1 text-xs" data-testid="costing-section">
      <div className="text-[10px] font-mono-num tracking-widest text-orange-300">// COSTING · ADMIN ONLY · {String(cb.billing_mode).toUpperCase()}</div>
      {cb.lines.map((l, i) => (
        <div key={i} className="flex justify-between" data-testid={`costing-line-${i}`}>
          <span className="text-slate-300">{l.item_name} · {l.kind === "hardware" ? `${n3(l.qty)} pcs` : `${n3(l.sqft)} sqft`} {l.rate != null ? `× ${r2(l.rate)}` : ""}</span>
          <span className="font-mono-num">{l.kind === "service" ? "—" : l.cost == null ? "Cost not recorded — no purchase entered for this sheet yet" : r2(l.cost)}</span>
        </div>
      ))}
      {w && <div className="text-slate-400" data-testid="costing-wholesale">Sheets used {n3(w.sheets_used)} · Raw {n3(w.raw_kg)} kg · Weighed {n3(w.weighed_kg)} kg · Scrap {n3(w.scrap_kg)} kg</div>}
      <div className="flex justify-between"><span className="text-slate-400">Bill (GST hata ke)</span><span className="font-mono-num">{r2(cb.bill)}</span></div>
      <div className="flex justify-between"><span className="text-slate-400">{cb.billing_mode === "job_work" ? "Machine + material cost" : "Material cost"}</span><span className="font-mono-num text-red-300">{r2(cb.material_cost + cb.machine_cost)}</span></div>
      <div className="flex justify-between"><span className="text-slate-400">Staff commission</span><span className="font-mono-num" data-testid="costing-staff-commission">{r2(cb.staff_commission)}</span></div>
      <div className="flex justify-between"><span className="text-slate-400">Welder commission</span><span className="font-mono-num" data-testid="costing-welder-commission">{r2(cb.welder_commission)}</span></div>
      <div className="flex justify-between border-t border-orange-500/30 pt-1"><span className="text-slate-300 font-bold">Profit</span>
        <span className="font-mono-num font-bold text-emerald-300" data-testid="costing-profit">{cb.profit == null ? "Cost not recorded" : r2(cb.profit)}</span></div>
    </div>
  );
}

export function ProfitLine({ order }) {
  const cb = order?.cost_breakdown;
  if (!cb) return null;
  return (
    <div className="mt-1 border-t border-dashed border-orange-500/50 pt-1 text-[10px] font-mono-num tracking-widest text-orange-300" data-testid={`profit-line-${order.id}`}>
      PROFIT · ADMIN {cb.profit == null ? "COST NOT RECORDED" : r2(cb.profit)}
    </div>
  );
}

export function CostingSettingsCard() {
  const [val, setVal] = React.useState("");
  const [saved, setSaved] = React.useState(null);
  React.useEffect(() => {
    import("@/lib/api").then(({ default: api }) => api.get("/settings/costing").then(({ data }) => { setVal(String(data.machine_cost_per_sqft)); setSaved(data.machine_cost_per_sqft); }).catch(() => {}));
  }, []);
  const save = async () => {
    const { default: api, formatApiError } = await import("@/lib/api");
    const { toast } = await import("sonner");
    try {
      const { data } = await api.put("/settings/costing", { machine_cost_per_sqft: Number(val) });
      setSaved(data.machine_cost_per_sqft); toast.success("Machine cost saved — sirf naye orders pe lagega");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="surface p-4 rounded-sm border-l-4 border-orange-500 flex items-end gap-3 flex-wrap" data-testid="costing-settings-card">
      <div>
        <div className="text-[10px] font-mono-num tracking-widest text-orange-300">// COSTING · ADMIN ONLY</div>
        <div className="text-sm text-slate-300">Job Work machine cost per sqft (sirf naye orders pe lagega)</div>
      </div>
      <input type="number" min="0" step="0.01" value={val} onChange={(e) => setVal(e.target.value)}
        className="h-9 w-28 bg-slate-800/60 border border-slate-600 text-white rounded-sm px-2 font-mono-num" data-testid="machine-cost-input" />
      <button onClick={save} className="h-9 px-4 rounded-sm bg-orange-500 text-white text-xs font-mono-num tracking-widest" data-testid="machine-cost-save">SAVE</button>
      {saved != null && <span className="text-[10px] font-mono-num text-slate-500">CURRENT ₹{saved}/SQFT</span>}
      <Phase3Migration />
    </div>
  );
}

function Phase3Migration() {
  const [rep, setRep] = React.useState(null);
  const run = async (dry) => {
    const { default: api, formatApiError } = await import("@/lib/api");
    const { toast } = await import("sonner");
    if (!dry && !window.confirm("Phase 3 APPLY? Sirf machine_cost_per_sqft = 18 setting banegi (agar nahi hai). Orders nahi chhue jayenge.")) return;
    try { const { data } = await api.post(`/admin/migrations/phase3-costing?dry_run=${dry}`); setRep(data); toast.success(dry ? "Phase 3 dry-run ready" : "Phase 3 applied"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="w-full flex items-center gap-2 flex-wrap" data-testid="phase3-migration">
      <span className="text-[10px] font-mono-num tracking-widest text-slate-400">PHASE 3 MIGRATION (backup Items → Sheet Master se lein):</span>
      <button onClick={() => run(true)} className="h-8 px-3 border border-cyan-500/50 text-cyan-300 rounded-sm text-[10px] font-mono-num" data-testid="phase3-dryrun-btn">DRY-RUN</button>
      <button onClick={() => run(false)} disabled={!rep?.dry_run} className="h-8 px-3 border border-orange-500 text-orange-200 rounded-sm text-[10px] font-mono-num disabled:opacity-40" data-testid="phase3-apply-btn">APPLY</button>
      {rep && (
        <span className={`text-[10px] font-mono-num ${rep.totals_match ? "text-emerald-400" : "text-red-400"}`} data-testid="phase3-report">
          {rep.dry_run ? "DRY-RUN" : "APPLIED"} · {rep.action.toUpperCase()} · ORDERS TOUCHED {rep.orders_touched} · MASTERS {rep.masters.length} · TOTALS {rep.totals_match ? "MATCH ✓" : "MISMATCH ✗"}
        </span>
      )}
    </div>
  );
}
